import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import { timesheetApi } from '../../../api/timesheetApi';
import TimesheetExportModal from '../../../components/tools/timesheet/TimesheetExportModal';
import TimesheetForm from '../../../components/tools/timesheet/TimesheetForm';
import TimesheetAdmin from '../../../components/tools/timesheet/TimesheetAdmin';
import '../../../components/tools/timesheet/Timesheet.css';

function TimesheetPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  // --- STATE: HAUPTFORMULAR (TAGESBERICHT) ---
  const [initialFormData, setInitialFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    project_id: null,
    tasks: [{ description: '', hours: '' }],
    notes: ''
  });
  const [entryId, setEntryId] = useState(null); // ID vorhanden = Bearbeitungsmodus

  // Speichert den Original-Zustand beim Laden zum Vergleich
  const [originalEntry, setOriginalEntry] = useState(null);

  // --- STATE: MODALS & LOGIK ---
  const [showExistsModal, setShowExistsModal] = useState(false);
  const [existingEntryInfo, setExistingEntryInfo] = useState(null);
  const [loadingForm, setLoadingForm] = useState(false);

  // --- STATE: HISTORY LISTE & FILTER ---
  const [entries, setEntries] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState('');
  const [toolSettings, setToolSettings] = useState(null);
  const [myPermissions, setMyPermissions] = useState(null);
  const [activeTab, setActiveTab] = useState('personal'); // 'personal' oder 'team'
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isFormExpanded, setIsFormExpanded] = useState(false);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    statuses: ['entwurf', 'eingereicht', 'überarbeitung_angefordert', 'genehmigt'],
    sortOrder: 'desc', // desc = Neu -> Alt, asc = Alt -> Neu
  });

  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  // --- STATE: AUFKLAPPEN ---
  const [expandedDates, setExpandedDates] = useState({});

  const toggleDate = (date) => {
    setExpandedDates(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  const getDayStatusClass = (dayEntries) => {
    if (dayEntries.some(e => e.deletion_request_pending)) return 'deletion_pending';
    if (dayEntries.some(e => e.status === 'entwurf')) return 'entwurf';
    if (dayEntries.some(e => e.status === 'überarbeitung_angefordert')) return 'überarbeitung_angefordert';
    if (dayEntries.every(e => e.status === 'genehmigt')) return 'genehmigt';
    return 'eingereicht';
  };

  const getUserStatusLabel = (entry) => {
    if (entry.deletion_request_pending) return "Wartet auf Löschung";
    switch (entry.status) {
      case 'eingereicht': return "Wartet auf Prüfung";
      case 'genehmigt': return "Genehmigt";
      case 'überarbeitung_angefordert': return "Nachbesserung nötig";
      default: return "Entwurf";
    }
  };

  const statusDropdownRef = useRef(null);

  const getLocalDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
    return localDate.toISOString().split('T')[0];
  };

  useEffect(() => {
    fetchEntriesWithFilters();
    fetchToolSettings();
    if (user) {
      fetchMyPermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- API CALLS & LOGIK ---

  const loadExistingEntryIntoForm = async (id) => {
    try {
      setLoadingForm(true);
      setShowExistsModal(false);
      setError('');

      const entry = await timesheetApi.getEntry(id);
      const dateStr = getLocalDate(entry.entry_date);

      let loadedTasks = [];
      if (entry.tasks) {
        loadedTasks = typeof entry.tasks === 'string' ? JSON.parse(entry.tasks) : entry.tasks;
      } else {
        loadedTasks = [{ description: entry.activity, hours: entry.hours }];
      }

      // State setzen
      setInitialFormData({
        date: dateStr,
        project_id: entry.project_id || null,
        tasks: loadedTasks,
        notes: entry.notes || ''
      });
      setEntryId(entry.id);
      setIsFormExpanded(true); // Auto-expand when editing

      // NEU (FIX): Deep Copy für Original, damit Referenzen getrennt sind!
      setOriginalEntry({
        date: dateStr,
        tasks: JSON.parse(JSON.stringify(loadedTasks)),
        notes: entry.notes || ''
      });

      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (err) {
      console.error(err);
      setError("Fehler beim Laden des Eintrags.");
    } finally {
      setLoadingForm(false);
    }
  };

  const fetchEntriesWithFilters = async () => {
    setLoadingList(true);
    setError('');
    try {
      if (filters.statuses.length === 0) {
        setEntries([]);
        return;
      }
      const statusParam = filters.statuses.join(',');
      const data = await timesheetApi.getEntries({
        startDate: filters.startDate,
        endDate: filters.endDate,
        status: statusParam,
      });
      setEntries(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingList(false);
    }
  };

  const fetchToolSettings = async () => {
    try {
      const data = await timesheetApi.getSettings();
      setToolSettings(data);
    } catch (err) { console.error(err); }
  };

  const fetchMyPermissions = async () => {
    try {
      const perms = await timesheetApi.getMyPermissions();
      setMyPermissions(perms);
    } catch (err) { console.error('Error fetching permissions', err); }
  };

  const resetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      statuses: ['entwurf', 'eingereicht', 'überarbeitung_angefordert', 'genehmigt'],
      sortOrder: 'desc'
    });
  };

  // --- FORM HANDLER ---

  const handleCancelEdit = () => {
    setEntryId(null);
    setInitialFormData({
      date: new Date().toISOString().split('T')[0],
      project_id: null,
      tasks: [{ description: '', hours: '' }],
      notes: ''
    });
    setOriginalEntry(null);
    setIsFormExpanded(false); // Collapse when cancelling
  };

  const handleFormSubmit = async (formData, isUpdate) => {
    const { date: selectedDate, project_id, tasks, notes } = formData;
    setLoadingForm(true);
    setError('');

    const hasEmpty = tasks.some(t => !t.description || !t.hours);
    if (hasEmpty) {
      setError("Bitte alle Tätigkeiten und Stunden ausfüllen.");
      setLoadingForm(false);
      return;
    }

    try {
      // --- 1. NEUER EINTRAG ---
      if (!isUpdate) {
        const checkRes = await timesheetApi.checkDate(selectedDate);

        if (checkRes.exists) {
          setExistingEntryInfo(checkRes.entry);
          setShowExistsModal(true);
          setLoadingForm(false);
          return;
        }

        await timesheetApi.createEntry({
          date: selectedDate, project_id, tasks, notes
        });

        showToast("Tagesbericht gesendet!", "success");
        setInitialFormData({
          date: new Date().toISOString().split('T')[0],
          project_id: null,
          tasks: [{ description: '', hours: '' }],
          notes: ''
        });
        setIsFormExpanded(false);
      }

      // --- 2. UPDATE (BEARBEITEN) ---
      else {
        // A: Dirty Check
        const isUnchanged =
          selectedDate === originalEntry.date &&
          project_id === (originalEntry.project_id || null) &&
          notes === originalEntry.notes &&
          JSON.stringify(tasks) === JSON.stringify(originalEntry.tasks);

        if (isUnchanged) {
          showToast("Keine Änderungen vorgenommen.", "warning");
          setLoadingForm(false);
          return;
        }

        // B: Speichern
        // Das Backend erkennt jetzt automatisch:
        // War es "Nachbesserung nötig"? -> Setzt Status direkt auf "Eingereicht".
        // War es "Entwurf"? -> Bleibt "Entwurf".
        await timesheetApi.updateEntry(entryId, {
          date: selectedDate, project_id, tasks, notes
        });

        // Feedback für den Nutzer
        if (originalEntry.status === 'überarbeitung_angefordert') {
          showToast("Korrektur gespeichert und automatisch eingereicht!", "success");
        } else if (originalEntry.status === 'genehmigt') {
          showToast("Änderung gespeichert! (Muss erneut genehmigt werden)", "success");
        } else {
          showToast("Eintrag gespeichert.", "success");
        }

        handleCancelEdit(); // Formular zurücksetzen
      }

      fetchEntriesWithFilters();

    } catch (err) {
      setError(err.response?.data?.error || "Fehler beim Speichern");
    } finally {
      setLoadingForm(false);
    }
  };

  // --- ACTIONS ---

  const handleDeleteEntry = async (id) => {
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    try {
      await timesheetApi.deleteEntry(id);
      fetchEntriesWithFilters();
      if (entryId === id) handleCancelEdit();
    } catch (err) { setError('Fehler beim Löschen'); }
  };

  const handleCancelDeletionRequest = async (id) => {
    try {
      await timesheetApi.cancelDeletionRequest(id);
      setEntries(entries.map(e => e.id === id ? { ...e, deletion_request_pending: false } : e));
    } catch (err) { showToast('Fehler beim Zurückziehen', 'error'); }
  };

  const handleSubmitEntry = async (id) => {
    try {
      await timesheetApi.submitEntry(id);
      fetchEntriesWithFilters();
    } catch (err) { setError('Fehler beim Einreichen'); }
  };

  const handleRequestDeletion = async (id) => {
    const reason = prompt('Grund für Löschungsanfrage (optional):');
    if (reason === null) return;
    try {
      await timesheetApi.requestDeletion(id, reason);
      setEntries(entries.map(e => e.id === id ? { ...e, deletion_request_pending: true } : e));
    } catch (err) { showToast(err.response?.data?.error || 'Fehler', 'error'); }
  };

  // --- HELPER ---

  const canEditEntry = (entryDate) => {
    if (!toolSettings) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(entryDate);
    checkDate.setHours(0, 0, 0, 0);
    if (checkDate >= today) return true;
    if (toolSettings.allow_edit_past_days === -1) return true;
    const daysDiff = Math.floor((today - checkDate) / (1000 * 60 * 60 * 24));
    return daysDiff <= toolSettings.allow_edit_past_days;
  };

  const getStatusButtonText = () => {
    if (filters.statuses.length === 0) return '0 ausgewählt';
    if (filters.statuses.length === 4) return 'Status: Alle';
    if (filters.statuses.length === 1) {
      const s = filters.statuses[0];
      return `Status: ${s === 'überarbeitung_angefordert' ? 'Nachbesserung' : s.charAt(0).toUpperCase() + s.slice(1)}`;
    }
    return `Status: ${filters.statuses.length} gewählt`;
  };

  const isFilterActive = filters.startDate !== '' ||
    filters.endDate !== '' ||
    filters.statuses.length !== 4 ||
    filters.sortOrder !== 'desc';

  const sortedEntries = [...entries].sort((a, b) => {
    const dateA = new Date(a.entry_date);
    const dateB = new Date(b.entry_date);
    return filters.sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  const entriesByDate = {};
  sortedEntries.forEach((entry) => {
    const date = getLocalDate(entry.entry_date);
    if (!entriesByDate[date]) entriesByDate[date] = [];
    entriesByDate[date].push(entry);
  });

  const getDayName = (dateString) => {
    const date = new Date(dateString);
    const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    return days[date.getDay()];
  };

  const hasTeamAccess = user?.role === 'admin' || (myPermissions && ['manager', 'reader', 'team_lead'].includes(myPermissions.role));

  return (
    <div className="timesheet-page-wrapper" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <main className="timesheet-main-content">
        <div className="timesheet-container">
          {/* Überschrift "Stundennachweis" entfernt wie gewünscht */}

          {hasTeamAccess && (
            <div className="timesheet-tabs" style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #e5e7eb', paddingBottom: '10px' }}>
              <button
                className={`tab-btn ${activeTab === 'personal' ? 'active' : ''}`}
                onClick={() => setActiveTab('personal')}
                style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: activeTab === 'personal' ? '#4f46e5' : '#f3f4f6', color: activeTab === 'personal' ? 'white' : '#4b5563', cursor: 'pointer', fontWeight: '500', transition: 'all 0.2s' }}
              >
                👤 Meine Einträge
              </button>
              <button
                className={`tab-btn ${activeTab === 'team' ? 'active' : ''}`}
                onClick={() => setActiveTab('team')}
                style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: activeTab === 'team' ? '#4f46e5' : '#f3f4f6', color: activeTab === 'team' ? 'white' : '#4b5563', cursor: 'pointer', fontWeight: '500', transition: 'all 0.2s' }}
              >
                👥 Mitarbeiter-Einträge
              </button>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          {activeTab === 'team' ? (
            <div className="team-timesheet-section">
              <TimesheetAdmin myPermissions={myPermissions} isEmbedded={true} />
            </div>
          ) : (
            <>
              {/* --- HAUPTFORMULAR --- */}
              <div className={`entry-form-card ${isFormExpanded ? 'expanded' : 'collapsed'}`}>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => setIsFormExpanded(!isFormExpanded)}
                  className="form-header-toggle"
                >
                  <h3 style={{ margin: 0 }}>{entryId ? '📝 Eintrag bearbeiten' : '✨ Neuer Tagesbericht'}</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {entryId && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancelEdit();
                        }}
                        className="btn-header-secondary"
                        style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                      >
                        Abbrechen / Neu
                      </button>
                    )}
                    <span className="expand-icon">{isFormExpanded ? '▲' : '▼'}</span>
                  </div>
                </div>

                {isFormExpanded && (
                  <TimesheetForm
                    initialData={initialFormData}
                    onSubmit={handleFormSubmit}
                    onCancel={entryId ? handleCancelEdit : undefined}
                    loading={loadingForm}
                    error={error}
                    entryId={entryId}
                  />
                )}
              </div>

              {/* --- HISTORIE & FILTER --- */}
              <div className={`entry-form-card history-section ${isHistoryExpanded ? 'expanded' : 'collapsed'}`}>
                <div
                  className="form-header-toggle"
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
                >
                  <h3 className="history-title" style={{ margin: 0 }}>📅 Historie</h3>
                  <span className="expand-icon">{isHistoryExpanded ? '▲' : '▼'}</span>
                </div>

                {isHistoryExpanded && (
                  <div className="history-content" style={{ marginTop: '20px' }}>
                    <div className="history-header">
                      <div className="timesheet-filter-bar">
                        <div className="filter-group">
                          <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>Von</span>
                          <input type="date" value={filters.startDate} onChange={e => setFilters({ ...filters, startDate: e.target.value })} />
                        </div>
                        <div className="filter-group">
                          <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>Bis</span>
                          <input type="date" value={filters.endDate} onChange={e => setFilters({ ...filters, endDate: e.target.value })} />
                        </div>

                        <div className="filter-group">
                          <div className="filter-dropdown-container" ref={statusDropdownRef}>
                            <button className="filter-dropdown-btn" onClick={() => setShowStatusDropdown(!showStatusDropdown)}>
                              <span>{getStatusButtonText()}</span>
                              <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>▼</span>
                            </button>
                            {showStatusDropdown && (
                              <div className="filter-dropdown-menu" style={{ width: '100%' }}>
                                {['entwurf', 'eingereicht', 'überarbeitung_angefordert', 'genehmigt'].map(status => (
                                  <div
                                    key={status}
                                    className="filter-option"
                                    onClick={() => {
                                      const newStatuses = filters.statuses.includes(status)
                                        ? filters.statuses.filter(s => s !== status)
                                        : [...filters.statuses, status];
                                      setFilters({ ...filters, statuses: newStatuses });
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={filters.statuses.includes(status)}
                                      readOnly
                                      style={{ cursor: 'pointer' }}
                                    />
                                    <span className="filter-label">
                                      {status === 'überarbeitung_angefordert' ? 'Nachbesserung' : status.charAt(0).toUpperCase() + status.slice(1)}
                                    </span>
                                  </div>
                                ))}
                                <div className="filter-actions-row">
                                  <small className="filter-action-btn action-all" onClick={() => setFilters({ ...filters, statuses: ['entwurf', 'eingereicht', 'überarbeitung_angefordert', 'genehmigt'] })}>Alle</small>
                                  <span style={{ margin: '0 8px', color: '#ddd' }}>|</span>
                                  <small className="filter-action-btn action-none" onClick={() => setFilters({ ...filters, statuses: [] })}>Keine</small>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="filter-group">
                          <select
                            className="filter-dropdown-btn"
                            value={filters.sortOrder}
                            onChange={(e) => setFilters({ ...filters, sortOrder: e.target.value })}
                            style={{ width: '130px' }}
                          >
                            <option value="desc">Neu → Alt</option>
                            <option value="asc">Alt → Neu</option>
                          </select>
                        </div>

                        <div className="filter-group" style={{ marginLeft: '5px' }}>
                          <button
                            onClick={resetFilters}
                            disabled={!isFilterActive}
                            className="btn-reset-icon"
                            title="Filter zurücksetzen"
                          >
                            ↺
                          </button>
                        </div>

                        <div className="filter-export-section">
                          <button
                            onClick={() => setIsExportOpen(true)}
                            className="btn-header-secondary"
                            style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', fontSize: '0.85rem', height: '38px' }}
                          >
                            <span style={{ fontSize: '1.1rem' }}>📄</span> PDF Export
                          </button>
                        </div>
                      </div>
                    </div>

                    {loadingList ? <p>Lade Liste...</p> : (
                      <div className="entries-list">
                        {Object.keys(entriesByDate).sort((a, b) => {
                          return filters.sortOrder === 'desc' ? new Date(b) - new Date(a) : new Date(a) - new Date(b);
                        }).map(date => {
                          const dayEntries = entriesByDate[date];
                          const mainEntry = dayEntries[0];
                          const dayTotal = dayEntries.reduce((sum, e) => sum + parseFloat(e.hours), 0);
                          const dayStatusClass = getDayStatusClass(dayEntries);
                          const dayStatusText = getUserStatusLabel(mainEntry);
                          const isExpanded = expandedDates[date];

                          return (
                            <div key={date} className={`day-section ${dayStatusClass}`}>
                              <div className="day-header" onClick={() => toggleDate(date)}>
                                <div className="day-info">
                                  <span className="toggle-icon">{isExpanded ? '▼' : '▶'}</span>
                                  <span className="date-text">{getDayName(date)}, {new Date(date).toLocaleDateString('de-DE')}</span>
                                </div>
                                <div className="day-meta">
                                  <span className={`status-text ${dayStatusClass}`}>{dayStatusText}</span>
                                  <span className="day-hours">{dayTotal} Std.</span>
                                </div>
                              </div>

                              {isExpanded && (() => {
                                let displayTasks = [];
                                try { displayTasks = typeof mainEntry.tasks === 'string' ? JSON.parse(mainEntry.tasks) : mainEntry.tasks; }
                                catch (e) { displayTasks = [{ description: mainEntry.activity, hours: mainEntry.hours }]; }
                                if (!displayTasks || displayTasks.length === 0) displayTasks = [{ description: mainEntry.activity, hours: mainEntry.hours }];

                                return (
                                  <div className="day-content">
                                    <div className="day-tasks-container">
                                      {mainEntry.project_name && (
                                        <div className="task-display-row" style={{ color: '#4f46e5', fontWeight: 600 }}>
                                          <span>📁 {mainEntry.project_name}</span>
                                        </div>
                                      )}
                                      {displayTasks.map((t, idx) => (
                                        <div key={idx} className="task-display-row">
                                          <span className="task-desc">{t.description}</span>
                                          <span className="task-time">{t.hours} Std.</span>
                                        </div>
                                      ))}
                                      {mainEntry.notes && (
                                        <div className="task-display-row" style={{ fontStyle: 'italic', color: '#888', fontSize: '0.85rem' }}>
                                          <span>📝 {mainEntry.notes}</span>
                                        </div>
                                      )}
                                    </div>

                                    <div className="day-actions-footer">
                                      {mainEntry.deletion_request_pending && (
                                        <div className="status-info-text" style={{ color: '#d97706' }}>⏳ Warte auf Admin...</div>
                                      )}
                                      {((mainEntry.status === 'entwurf' || canEditEntry(mainEntry.entry_date))) && (
                                        <button onClick={() => loadExistingEntryIntoForm(mainEntry.id)} className="action-btn edit-btn">✏️ Bearbeiten</button>
                                      )}
                                      {mainEntry.status === 'entwurf' && (
                                        <button onClick={() => handleSubmitEntry(mainEntry.id)} className="action-btn submit-btn">📤 Einreichen</button>
                                      )}
                                      {mainEntry.status === 'entwurf' ? (
                                        <button onClick={() => handleDeleteEntry(mainEntry.id)} className="action-btn delete-btn">🗑️ Löschen</button>
                                      ) : (
                                        mainEntry.deletion_request_pending ? (
                                          <button onClick={() => handleCancelDeletionRequest(mainEntry.id)} className="action-btn" style={{ background: '#fff7ed', color: '#c2410c', borderColor: '#fed7aa' }}>↩️ Löschung zurücknehmen</button>
                                        ) : (
                                          <button onClick={() => handleRequestDeletion(mainEntry.id)} className="action-btn delete-request-btn">🚩 Löschung anfordern</button>
                                        )
                                      )}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {showExistsModal && (
                <div className="modal-overlay">
                  <div className="modal-content" style={{ maxWidth: '400px' }}>
                    <h3>⚠️ Eintrag existiert bereits</h3>
                    <p>Für den <strong>{new Date(existingEntryInfo?.entry_date).toLocaleDateString()}</strong> liegt bereits ein Eintrag vor.</p>
                    <p>Status: <strong>{existingEntryInfo?.status?.toUpperCase()}</strong></p>
                    <div className="modal-actions">
                      <button onClick={() => { setShowExistsModal(false); }} className="btn-cancel">Abbrechen</button>
                      <button onClick={() => loadExistingEntryIntoForm(existingEntryInfo.id)} className="btn-primary">Diesen Eintrag bearbeiten</button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <TimesheetExportModal
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        entries={entries}
        user={user}
        title="Persönlicher Stundennachweis"
      />
    </div>
  );
}

export default TimesheetPage;